/**
 * PostHog product analytics, session replay, and feature flags.
 *
 * Everything PostHog-specific lives in this module:
 * - `PostHogRoot` is mounted once at the app root (src/entry-client.tsx). It
 *   lazy-loads posthog-js at idle time and provides the client through the
 *   official posthog-js/react context.
 * - `capturePostHogEvent` is the destination used by `trackEvent()` in
 *   src/utils/trackingManager.ts.
 * - `usePostHogIdentity` is called by AuthProvider (src/hooks/useAuth.tsx).
 * - `useFeatureFlag` is the null-safe flag hook for components.
 *
 * Rules this module enforces (founder decision, Oct 1 2026: our own product
 * analytics counts everyone except people who opted out):
 * - No `VITE_POSTHOG_KEY` means a full no-op: posthog-js is never imported, no
 *   network, no storage.
 * - Three separate gates:
 *   1. Product analytics (capture): on unless the person opted out
 *      (`hasProductAnalyticsConsent()`: Your Privacy Choices, a web cookie
 *      decline, the Android toggle, GPC, or an account-level opt-out applied
 *      at sign-in). An iOS ATT decline does not turn it off.
 *   2. Advertising and attribution (GA4, Meta, AppsFlyer...) keep
 *      `hasTrackingConsent()`, which follows ATT on iOS. Not used here.
 *   3. Session replay: only after an explicit Allow
 *      (`hasExplicitAnalyticsGrant()`); implicit US consent, a pending choice
 *      and ATT "Allow" never start a recording.
 *   An opt-out is applied live via `TRACKING_CONSENT_EVENT`, falling back to
 *   memory persistence.
 * - Persistence: web uses localStorage+cookie, native uses localStorage only
 *   (no cookies in the app).
 * - Local web dev hosts and sensitive auth callback URLs never load PostHog.
 * - Internal test users, internal devices and internal builds (QA bundles,
 *   debug, TestFlight, sideloaded) never capture.
 * - Only the Supabase user id is sent on identify. No email, names, or phone.
 *   No IP is stored (project setting "Discard client IP data", applied after
 *   GeoIP) and URLs lose ad click ids.
 * - Web goes through the first-party `/ingest` proxy (vercel.json); native
 *   keeps the absolute PostHog host.
 * - Every event carries `platform` (web/ios/android), `app_version` (plus
 *   `app_build` on native, see src/utils/appVersion.ts), `is_internal_build`
 *   and, once the profile has loaded, `subscription_status` as super
 *   properties.
 *
 * posthog-js is ~100 KB gzip, so it is loaded with a dynamic import instead of
 * landing in the main chunk, together with ./posthogPrivacy.ts (SDK config
 * and the property filters). Calls made before it loads are queued (bounded)
 * and replayed once it initializes; queued events are filtered when they
 * replay, so nothing reaches the SDK unfiltered.
 */
import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import type { PostHog } from 'posthog-js';
// The slim entry is the same React integration without a static import of
// posthog-js itself, which keeps the SDK out of the main chunk.
import { PostHogContext, usePostHog } from 'posthog-js/react/slim';
import { env } from '@/lib/env';
import { getAppVersionInfo, peekAppVersionInfo, resolveAppVersionInfo } from '@/utils/appVersion';
import {
  TRACKING_CONSENT_EVENT,
  hasTrackingConsent,
  isConsentOptInJurisdiction,
  isInternalAnalyticsTestUser,
  isLocalWebTrackingEnvironment,
  revokeTrackingConsent,
} from '@/utils/trackingManager';
import {
  hasExplicitAnalyticsGrant,
  hasProductAnalyticsConsent,
  startAccountAnalyticsSyncRetry,
  syncAccountAnalyticsOptOut,
} from '@/utils/productAnalyticsConsent';
import {
  getCachedInstallEnvironment,
  isInternalAnalyticsBuild,
  loadInstallEnvironment,
  markInternalAnalyticsDevice,
} from '@/utils/analyticsBuildEnvironment';
import { shouldSuppressSensitiveAuthTelemetry } from '@/utils/urlPrivacy';
import type { PostHogDecision } from './posthogPrivacy';

export const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';
/** First-party reverse proxy path on web (see the /ingest rewrites in vercel.json). */
export const POSTHOG_WEB_PROXY_PATH = '/ingest';
export const POSTHOG_US_UI_HOST = 'https://us.posthog.com';
const MAX_PENDING_CALLS = 100;
const IDLE_INIT_TIMEOUT_MS = 2000;
const IDLE_INIT_FALLBACK_MS = 1500;
// The native version comes over the Capacitor bridge; never hold up init on it.
const APP_VERSION_WAIT_MS = 1000;

type PendingCall = (client: PostHog) => void;

let client: PostHog | null = null;
// Loaded with posthog-js and set before `client`, so it is always present
// once the client is.
let privacy: typeof import('./posthogPrivacy') | null = null;
let loadPromise: Promise<PostHog | null> | null = null;
let pendingCalls: PendingCall[] = [];
let consentListenerAttached = false;
let identifiedUserId: string | null = null;
let identifiedUserIsInternal = false;
let subscriptionStatus: string | null = null;
// Last decision applied through set_config, so repeated consent or cross-tab
// storage events do not reconfigure the SDK when nothing changed.
let appliedDecisionKey: string | null = null;
// Sign-in order: the account-level analytics opt-out is read before the
// person is identified. While that read is pending or failed, nothing is
// captured (capturePostHogEvent refuses and before_send drops SDK events).
type IdentityGateState = 'pending' | 'clear' | 'blocked';
let identityGate: { userId: string; state: IdentityGateState } | null = null;
let identityRetryAttached = false;
type IdentityWaiter = { userId: string; resolve: (state: 'clear' | 'blocked') => void };
let identityWaiters: IdentityWaiter[] = [];
const clientListeners = new Set<() => void>();
const storeLinkListeners = new Set<() => void>();
// Web to app identity link (#1353). Storage keys live for the install; the
// in-memory copies cover a device where storage is blocked.
const WEB_IDENTITY_LINKED_KEY = 'jp:posthog_web_identity_linked';
const WEB_IDENTITY_PENDING_KEY = 'jp:posthog_web_identity_pending';
type WebIdentityLinkDetails = {
  media_source: string | null;
  campaign: string | null;
  utm_source: string | null;
};
type PendingWebIdentityLink = WebIdentityLinkDetails & { webDistinctId: string };
let webIdentityLinkedInMemory = false;
let pendingWebIdentityLinkInMemory: PendingWebIdentityLink | null = null;

function readPostHogKey(): string | null {
  const key = env.posthogKey?.trim();
  return key ? key : null;
}

function readPostHogHost(): string {
  const host = env.posthogHost?.trim();
  return host ? host.replace(/\/+$/, '') : DEFAULT_POSTHOG_HOST;
}

function isNativeRuntime(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Where posthog-js sends events. Web uses the same-origin /ingest proxy so
 * blockers that drop third-party analytics hosts do not lose events; the
 * proxy only targets US cloud, so another configured host is used directly.
 * Native (capacitor:// or https://localhost origins) keeps the absolute host.
 */
export function resolvePostHogApiHosts(): { apiHost: string; uiHost?: string } {
  const host = readPostHogHost();
  if (isNativeRuntime() || host !== DEFAULT_POSTHOG_HOST) return { apiHost: host };
  return { apiHost: POSTHOG_WEB_PROXY_PATH, uiHost: POSTHOG_US_UI_HOST };
}

/** True when a project key is configured. Without it every export is a no-op. */
export function isPostHogConfigured(): boolean {
  return readPostHogKey() !== null;
}

function canLoadPostHog(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isPostHogConfigured()) return false;
  if (isLocalWebTrackingEnvironment()) return false;
  if (shouldSuppressSensitiveAuthTelemetry()) return false;
  return true;
}

/**
 * Whether PostHog may capture right now: product analytics is on unless the
 * person opted out, and never for internal users, devices or builds. ATT is
 * not part of this gate.
 */
function isCaptureAllowed(): boolean {
  if (!hasProductAnalyticsConsent()) return false;
  if (shouldSuppressSensitiveAuthTelemetry()) return false;
  if (identifiedUserIsInternal) return false;
  if (isInternalAnalyticsTestUser()) return false;
  if (isInternalAnalyticsBuild()) return false;
  return true;
}

/** Capture, replay (capture plus an explicit Allow) and persistence. */
function currentDecision(): PostHogDecision {
  const capture = isCaptureAllowed();
  return {
    capture,
    replay: capture && hasExplicitAnalyticsGrant(),
    persistence: !capture
      ? 'memory'
      : isNativeRuntime()
        ? // No cookies inside the native app.
          'localStorage'
        : // EU/EEA and unknown-country visitors who have not chosen yet are
          // counted for this page load only: nothing is stored on their device
          // until they allow analytics.
          isConsentOptInJurisdiction() && !hasExplicitAnalyticsGrant()
          ? 'memory'
          : 'localStorage+cookie',
  };
}

function decisionKey(decision: PostHogDecision): string {
  return `${decision.capture}|${decision.replay}|${decision.persistence}`;
}

/**
 * The sign-in account opt-out check and the account it is for: null when no
 * check is running (signed out, or not started yet).
 * Sentry uses this to apply the same gate as PostHog.
 */
export function getAnalyticsIdentityGate(): { userId: string; state: IdentityGateState } | null {
  return identityGate ? { ...identityGate } : null;
}

function isIdentityGateOpen(): boolean {
  return identityGate === null || identityGate.state === 'clear';
}

function settleIdentityWaiters(): void {
  const gate = identityGate;
  if (!gate || gate.state === 'pending') return;
  const settled = identityWaiters.filter((waiter) => waiter.userId === gate.userId);
  if (settled.length === 0) return;
  identityWaiters = identityWaiters.filter((waiter) => waiter.userId !== gate.userId);
  for (const waiter of settled) waiter.resolve(gate.state);
}

/**
 * Resolves once the sign-in check of this account's analytics opt-out has
 * finished (see resolvePostHogIdentity): 'clear' when it was read, 'blocked'
 * when the read failed or did not finish within `timeoutMs`. Events that must
 * respect an opt-out made on another device (sign_up) wait for this and are
 * only sent on 'clear', after checking the now-applied device choice. Works
 * without a PostHog key: the account check runs for every signed-in member.
 */
export function waitForAnalyticsIdentity(
  userId: string,
  timeoutMs = 15_000
): Promise<'clear' | 'blocked'> {
  if (identityGate?.userId === userId && identityGate.state !== 'pending') {
    return Promise.resolve(identityGate.state);
  }
  return new Promise((resolve) => {
    const waiter: IdentityWaiter = {
      userId,
      resolve: (state) => {
        clearTimeout(timer);
        resolve(state);
      },
    };
    const timer = setTimeout(() => {
      identityWaiters = identityWaiters.filter((entry) => entry !== waiter);
      resolve('blocked');
    }, timeoutMs);
    identityWaiters.push(waiter);
  });
}

// ---------------------------------------------------------------------------
// Client lifecycle
// ---------------------------------------------------------------------------

function registerSuperProperties(posthog: PostHog): void {
  const version = peekAppVersionInfo();
  posthog.register({
    platform: Capacitor.getPlatform(),
    ...(version ? { app_version: version.appVersion } : {}),
    ...(version?.appBuild ? { app_build: version.appBuild } : {}),
  });
  // Capture never runs for internal builds, so this is false on every event
  // that arrives; it exists so PostHog filters can rely on it.
  const installChannel = getCachedInstallEnvironment()?.channel;
  posthog.register({
    is_internal_build: isInternalAnalyticsBuild(),
    ...(installChannel ? { install_channel: installChannel } : {}),
  });
  if (subscriptionStatus) {
    posthog.register({ subscription_status: subscriptionStatus });
  } else {
    posthog.unregister('subscription_status');
  }
}

function notifyClientListeners(): void {
  clientListeners.forEach((listener) => listener());
  notifyStoreLinkListeners();
}

function notifyStoreLinkListeners(): void {
  storeLinkListeners.forEach((listener) => listener());
}

function flushPendingCalls(posthog: PostHog): void {
  const calls = pendingCalls;
  pendingCalls = [];
  for (const call of calls) {
    try {
      call(posthog);
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[PostHog] queued call failed:', error);
    }
  }
}

function withClient(call: PendingCall): void {
  if (client) {
    try {
      call(client);
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[PostHog] call failed:', error);
    }
    return;
  }
  if (!canLoadPostHog()) return;
  if (pendingCalls.length >= MAX_PENDING_CALLS) pendingCalls.shift();
  pendingCalls.push(call);
}

function handleConsentChange(): void {
  if (!client) {
    // Not loaded yet because consent was missing at idle time: load now.
    if (isCaptureAllowed()) void initPostHog();
    return;
  }
  syncPostHogConsent();
  // Store links add or drop the web PostHog id with consent.
  notifyStoreLinkListeners();
}

function attachConsentListener(): void {
  if (consentListenerAttached || typeof window === 'undefined') return;
  window.addEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);
  window.addEventListener('storage', handleConsentChange);
  consentListenerAttached = true;
}

/**
 * Applies the current consent decision to the loaded SDK. Safe to call any
 * time; a no-op until posthog-js has loaded.
 */
export function syncPostHogConsent(): void {
  const posthog = client;
  if (!posthog) return;

  const decision = currentDecision();
  const key = decisionKey(decision);
  const decisionChanged = appliedDecisionKey !== key;
  appliedDecisionKey = key;

  if (decision.capture) {
    if (decisionChanged) {
      posthog.set_config({
        persistence: decision.persistence,
        disable_session_recording: !decision.replay,
      });
    }
    // Persist an opt-in only to undo an explicit opt-out or to record an
    // explicit Allow; implicitly counted visitors keep no consent marker.
    if (
      posthog.has_opted_out_capturing() ||
      (decision.replay && !posthog.has_opted_in_capturing())
    ) {
      posthog.opt_in_capturing({ captureEventName: false });
    }
    if (identifiedUserId && posthog.get_distinct_id() !== identifiedUserId) {
      posthog.identify(identifiedUserId);
    }
    applyPendingWebIdentityLink(posthog);
    return;
  }

  if (!posthog.has_opted_out_capturing()) {
    posthog.opt_out_capturing();
  }
  if (decisionChanged) {
    // Switching back to memory clears PostHog's cookie and localStorage entry.
    posthog.set_config({ persistence: 'memory', disable_session_recording: true });
  }
}

/**
 * Lazy-loads and initializes posthog-js once. Resolves to null when PostHog
 * is disabled (no key, local dev, sensitive auth URL) or fails to load.
 */
export function initPostHog(): Promise<PostHog | null> {
  if (client) return Promise.resolve(client);
  if (loadPromise) return loadPromise;

  const key = readPostHogKey();
  if (!key || !canLoadPostHog()) return Promise.resolve(null);

  // Native: learn whether this is a debug, TestFlight or sideloaded install
  // before anything is sent (bounded to about a second).
  const environmentReady = isNativeRuntime()
    ? loadInstallEnvironment().then(() => undefined)
    : Promise.resolve();

  loadPromise = environmentReady
    .then(() => {
      // People who opted out, and internal builds, never contact PostHog at
      // all (no flag or remote-config fetch). Load if that changes; until
      // then feature flags return their defaults.
      if (!isCaptureAllowed()) {
        attachConsentListener();
        // Calls queued before the install check (an internal build) are dropped.
        pendingCalls = [];
        loadPromise = null;
        return null;
      }
      return Promise.all([
        import('posthog-js'),
        import('./posthogPrivacy'),
        resolveAppVersionInfo(APP_VERSION_WAIT_MS),
      ]).then(([{ default: posthog }, privacyModule]) => {
        const decision = currentDecision();
        posthog.init(
          key,
          privacyModule.buildPostHogConfig(decision, resolvePostHogApiHosts(), isIdentityGateOpen)
        );
        appliedDecisionKey = decisionKey(decision);
        privacy = privacyModule;
        client = posthog;
        registerSuperProperties(posthog);
        attachConsentListener();
        syncPostHogConsent();
        flushPendingCalls(posthog);
        notifyClientListeners();
        // A slow native bridge missed the wait above: register once it answers.
        if (!peekAppVersionInfo()) {
          void getAppVersionInfo().then(() => {
            if (client === posthog) registerSuperProperties(posthog);
          });
        }
        return posthog;
      });
    })
    .catch((error: unknown) => {
      if (import.meta.env.DEV) console.warn('[PostHog] failed to load:', error);
      pendingCalls = [];
      loadPromise = null;
      return null;
    });

  return loadPromise;
}

/** The loaded client, or null before load / when disabled. */
export function getPostHogClient(): PostHog | null {
  return client;
}

function subscribeToPostHogClient(listener: () => void): () => void {
  clientListeners.add(listener);
  return () => {
    clientListeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Events and identity
// ---------------------------------------------------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Captures an event. Called by trackEvent() with sanitized params plus
 * event_id. Route page views (`page_view`) become PostHog `$pageview`. A
 * UUID event_id becomes the PostHog event uuid (the same uuid track-event
 * would use), so a server copy dedupes. Returns true when the event was
 * captured or queued for the loading SDK.
 */
export function capturePostHogEvent(
  eventName: string,
  properties?: Record<string, unknown>
): boolean {
  if (!isPostHogConfigured()) return false;
  if (!isCaptureAllowed()) return false;
  if (!isIdentityGateOpen()) return false;
  if (!client && !canLoadPostHog()) return false;
  const name = eventName === 'page_view' ? '$pageview' : eventName;
  // Filtered when the call runs (right away once loaded, else when the queue
  // replays), with the privacy filters that load alongside posthog-js.
  const snapshot = properties ? { ...properties } : undefined;
  const eventId = properties?.event_id;
  const options =
    typeof eventId === 'string' && UUID_PATTERN.test(eventId) ? { uuid: eventId } : undefined;
  withClient((posthog) => {
    if (!privacy) return;
    const safeProperties = privacy.toPostHogProperties(snapshot);
    if (options) {
      posthog.capture(name, safeProperties, options);
    } else {
      posthog.capture(name, safeProperties);
    }
  });
  return true;
}

/**
 * Links events to the Supabase user id. The email is used only to detect
 * internal test accounts and is never sent to PostHog.
 */
export function identifyPostHogUser(
  userId: string,
  options?: { email?: string | null; subscriptionStatus?: string | null }
): void {
  if (!isPostHogConfigured()) return;
  identifiedUserId = userId;
  identifiedUserIsInternal = isInternalAnalyticsTestUser(options?.email ?? undefined);
  // A QA phone keeps its internal mark after the tester signs out and starts
  // a guest session, which has no email to match.
  if (identifiedUserIsInternal) markInternalAnalyticsDevice();
  subscriptionStatus = options?.subscriptionStatus ?? null;
  withClient((posthog) => {
    registerSuperProperties(posthog);
    syncPostHogConsent();
  });
}

/** Clears the PostHog identity on sign-out so the next user starts fresh. */
export function resetPostHogUser(): void {
  if (!isPostHogConfigured()) return;
  identifiedUserId = null;
  identifiedUserIsInternal = false;
  subscriptionStatus = null;
  withClient((posthog) => {
    posthog.reset();
    // reset() also clears super properties and stored consent; re-apply both.
    registerSuperProperties(posthog);
    syncPostHogConsent();
  });
}

type IdentityDetails = () => { email: string | null; subscriptionStatus: string | null };

function retryBlockedIdentity(): void {
  const gate = identityGate;
  if (!gate || gate.state !== 'blocked' || !lastIdentityDetails) return;
  void resolvePostHogIdentity(gate.userId, lastIdentityDetails);
}

function attachIdentityRetry(): void {
  if (identityRetryAttached || typeof window === 'undefined') return;
  window.addEventListener('focus', retryBlockedIdentity);
  window.addEventListener('online', retryBlockedIdentity);
  identityRetryAttached = true;
}

let lastIdentityDetails: IdentityDetails | null = null;

/**
 * Sign-in order: read the account-level analytics opt-out first (applying it
 * to this device, or pushing a device opt-out to the account), and only then
 * identify. Nothing is captured while the read is pending; a failed read
 * keeps capture off (fail closed) and retries on focus or reconnect. A
 * person who opted out is never identified.
 */
export async function resolvePostHogIdentity(
  userId: string,
  details: IdentityDetails
): Promise<IdentityGateState> {
  identityGate = { userId, state: 'pending' };
  lastIdentityDetails = details;
  const outcome = await syncAccountAnalyticsOptOut(userId, () =>
    revokeTrackingConsent({ syncAccount: false })
  );
  // A sign-out or another sign-in replaced this resolution.
  if (identityGate?.userId !== userId) return 'blocked';
  if (outcome === 'error') {
    identityGate = { userId, state: 'blocked' };
    attachIdentityRetry();
    settleIdentityWaiters();
    return 'blocked';
  }
  identityGate = { userId, state: 'clear' };
  settleIdentityWaiters();
  // Identifies only while capture is allowed: an opted-out device (including
  // an account opt-out just applied) never calls posthog.identify().
  const { email, subscriptionStatus: status } = details();
  identifyPostHogUser(userId, { email, subscriptionStatus: status });
  return 'clear';
}

/**
 * Keeps PostHog identity in sync with the signed-in Supabase user. On sign-in
 * (including a restored session) it resolves the account opt-out, then
 * identifies; it resets on sign-out or when a different user signs in.
 * `subscriptionStatus` (the profile's subscription_tier) becomes a super
 * property so events split by plan.
 */
export function usePostHogIdentity(
  user: { id: string; email?: string | null } | null,
  subscriptionStatus?: string | null
): void {
  const previousUserIdRef = React.useRef<string | null>(null);
  const userId = user?.id ?? null;
  const email = user?.email ?? null;
  const status = subscriptionStatus ?? null;
  const detailsRef = React.useRef({ email, subscriptionStatus: status });
  // Declared before the sign-in effect, so it runs first on each commit.
  React.useEffect(() => {
    detailsRef.current = { email, subscriptionStatus: status };
  }, [email, status]);

  React.useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (userId) {
      if (previousUserId && previousUserId !== userId) resetPostHogUser();
      void resolvePostHogIdentity(userId, () => detailsRef.current);
      return;
    }
    identityGate = null;
    if (previousUserId) resetPostHogUser();
  }, [userId]);

  // Profile updates (email, plan) after the gate cleared.
  React.useEffect(() => {
    if (!userId || identityGate?.userId !== userId || identityGate.state !== 'clear') return;
    identifyPostHogUser(userId, { email, subscriptionStatus: status });
  }, [userId, email, status]);
}

// ---------------------------------------------------------------------------
// Web to app identity (#1353)
// ---------------------------------------------------------------------------

/**
 * posthog-js anonymous ids (UUIDv7) and Supabase user ids are both UUIDs.
 * Anything else in deep_link_sub1 (a route, a campaign code) is not ours.
 */
export function isLinkablePostHogDistinctId(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/**
 * The web visitor's anonymous PostHog id for the App Store and Play links
 * (sent as deep_link_sub1), so the app can link its person to this one.
 * Null on native, before posthog-js loads, after an analytics opt-out, while
 * ad and attribution consent is not granted (the link goes through
 * AppsFlyer), and for a signed-in member (their user id never goes in a URL;
 * signing in on the app links them anyway).
 */
export function getPostHogDistinctIdForStoreLink(): string | null {
  const posthog = client;
  if (!posthog || isNativeRuntime()) return null;
  if (!isCaptureAllowed() || !isIdentityGateOpen() || !hasTrackingConsent()) return null;
  try {
    if (posthog.has_opted_out_capturing()) return null;
    if (identifiedUserId || posthog.get_property('$user_state') === 'identified') return null;
    const distinctId = posthog.get_distinct_id();
    return isLinkablePostHogDistinctId(distinctId) ? distinctId : null;
  } catch {
    return null;
  }
}

/**
 * Calls `listener` whenever getPostHogDistinctIdForStoreLink may have
 * changed: posthog-js finished loading, or a consent choice was applied.
 */
export function subscribeToStoreLinkDistinctId(listener: () => void): () => void {
  storeLinkListeners.add(listener);
  return () => {
    storeLinkListeners.delete(listener);
  };
}

function isWebIdentityLinked(): boolean {
  if (webIdentityLinkedInMemory) return true;
  try {
    return window.localStorage.getItem(WEB_IDENTITY_LINKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markWebIdentityLinked(): void {
  webIdentityLinkedInMemory = true;
  pendingWebIdentityLinkInMemory = null;
  try {
    window.localStorage.setItem(WEB_IDENTITY_LINKED_KEY, '1');
    window.localStorage.removeItem(WEB_IDENTITY_PENDING_KEY);
  } catch {
    // The in-memory mark still holds for this app session.
  }
}

function readPendingWebIdentityLink(): PendingWebIdentityLink | null {
  try {
    const raw = window.localStorage.getItem(WEB_IDENTITY_PENDING_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PendingWebIdentityLink>;
      if (isLinkablePostHogDistinctId(parsed.webDistinctId)) {
        return {
          webDistinctId: parsed.webDistinctId,
          media_source: typeof parsed.media_source === 'string' ? parsed.media_source : null,
          campaign: typeof parsed.campaign === 'string' ? parsed.campaign : null,
          utm_source: typeof parsed.utm_source === 'string' ? parsed.utm_source : null,
        };
      }
    }
  } catch {
    // Fall back to the in-memory copy.
  }
  return pendingWebIdentityLinkInMemory;
}

/**
 * Runs the pending link once capture is allowed and the sign-in opt-out
 * check has cleared. Called from syncPostHogConsent, so a link that arrived
 * before consent or before the SDK loaded is applied when they do.
 */
function applyPendingWebIdentityLink(posthog: PostHog): void {
  if (!isNativeRuntime()) return;
  if (!isCaptureAllowed() || !isIdentityGateOpen()) return;
  const pending = readPendingWebIdentityLink();
  if (!pending) return;
  if (isWebIdentityLinked()) {
    markWebIdentityLinked();
    return;
  }
  try {
    // Mark first: a throw below must not lead to a second alias later.
    markWebIdentityLinked();
    if (posthog.get_distinct_id() === pending.webDistinctId) return;
    // posthog.alias(alias) sends $create_alias with { alias, distinct_id:
    // the current id }, so PostHog adds the web id to this app person and
    // merges the web person (with its utm_source) into it. Passing the
    // current id as the alias would turn the call into identify().
    posthog.alias(pending.webDistinctId);
    const properties = {
      web_distinct_id_present: true,
      media_source: pending.media_source,
      campaign: pending.campaign,
      utm_source: pending.utm_source,
      platform: Capacitor.getPlatform(),
    };
    posthog.capture(
      'web_to_app_identity_linked',
      privacy ? privacy.toPostHogProperties(properties) : properties
    );
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[PostHog] web identity link failed:', error);
  }
}

/**
 * Native: links this install's PostHog person to the web person whose id
 * came back in an AppsFlyer conversion or deep link payload (deep_link_sub1),
 * once per install. Kept in storage until PostHog has loaded and analytics
 * is allowed. The web id goes only into the alias call here, never into a
 * person property or the web_to_app_identity_linked event.
 */
export function linkPostHogWebIdentity(webDistinctId: string, details: WebIdentityLinkDetails): void {
  if (typeof window === 'undefined' || !isNativeRuntime() || !isPostHogConfigured()) return;
  if (!isLinkablePostHogDistinctId(webDistinctId)) return;
  if (isWebIdentityLinked()) return;
  const pending: PendingWebIdentityLink = {
    webDistinctId,
    media_source: details.media_source,
    campaign: details.campaign,
    utm_source: details.utm_source,
  };
  pendingWebIdentityLinkInMemory = pending;
  try {
    window.localStorage.setItem(WEB_IDENTITY_PENDING_KEY, JSON.stringify(pending));
  } catch {
    // The in-memory copy is used instead.
  }
  if (client) applyPendingWebIdentityLink(client);
}

// ---------------------------------------------------------------------------
// React wiring
// ---------------------------------------------------------------------------

function scheduleIdle(task: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(task, { timeout: IDLE_INIT_TIMEOUT_MS });
    return () => idleWindow.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(task, IDLE_INIT_FALLBACK_MS);
  return () => window.clearTimeout(handle);
}

/**
 * Root provider. Renders the posthog-js/react context around the app and
 * loads the SDK at idle time. The context element is always rendered (with no
 * client until load) so the app tree never remounts when PostHog arrives.
 */
export function PostHogRoot({ children }: { children: React.ReactNode }): React.ReactElement {
  // State plus a transition, not useSyncExternalStore: the client arrives at
  // idle time, often while the lazy route of a server-rendered page is still
  // hydrating. A store change re-renders at sync priority, and this context
  // reaches that route's Suspense boundary, so React threw away the server
  // HTML and drew the page again (minified error #421, #1301). A transition
  // waits for hydration instead. State starts at null, like the old server
  // snapshot, so the first client render always matches the server HTML.
  const [loadedClient, setLoadedClient] = React.useState<PostHog | null>(null);

  React.useEffect(() => {
    const sync = () => {
      const next = getPostHogClient();
      React.startTransition(() => setLoadedClient(next));
    };
    sync();
    return subscribeToPostHogClient(sync);
  }, []);

  React.useEffect(() => {
    // An analytics choice that could not be saved on the account is retried
    // on app start, focus and reconnect (it applies on this device meanwhile).
    startAccountAnalyticsSyncRetry();
    if (!canLoadPostHog()) return;
    return scheduleIdle(() => {
      void initPostHog();
    });
  }, []);

  const value = React.useMemo(
    () => ({
      // posthog-js/react/slim's own default context also has no client until
      // a provider supplies one. Use useFeatureFlag below, which handles that.
      client: (loadedClient ?? undefined) as PostHog,
      bootstrap: loadedClient?.config?.bootstrap,
    }),
    [loadedClient]
  );

  return React.createElement(PostHogContext.Provider, { value }, children);
}

export type FeatureFlagValue = boolean | string;

function readFeatureFlag(
  posthog: PostHog | undefined,
  key: string
): FeatureFlagValue | undefined {
  if (!posthog) return undefined;
  try {
    return posthog.getFeatureFlag(key);
  } catch {
    return undefined;
  }
}

/**
 * Feature flag / experiment value for `key`: `true`/`false` for boolean flags,
 * the variant key for multivariate flags. Returns `defaultValue` while
 * PostHog is disabled, loading, or the flag is unknown.
 */
export function useFeatureFlag<T extends FeatureFlagValue = boolean>(
  key: string,
  defaultValue: T = false as T
): FeatureFlagValue | T {
  const posthog = usePostHog() as PostHog | undefined;
  const [value, setValue] = React.useState<FeatureFlagValue | undefined>(() =>
    readFeatureFlag(posthog, key)
  );

  React.useEffect(() => {
    setValue(readFeatureFlag(posthog, key));
    if (!posthog) return;
    return posthog.onFeatureFlags(() => {
      setValue(readFeatureFlag(posthog, key));
    });
  }, [posthog, key]);

  return value ?? defaultValue;
}

/** Test-only: restores module state between tests. */
export function __resetPostHogForTests(): void {
  if (consentListenerAttached && typeof window !== 'undefined') {
    window.removeEventListener(TRACKING_CONSENT_EVENT, handleConsentChange);
    window.removeEventListener('storage', handleConsentChange);
  }
  client = null;
  privacy = null;
  loadPromise = null;
  pendingCalls = [];
  consentListenerAttached = false;
  identifiedUserId = null;
  identifiedUserIsInternal = false;
  subscriptionStatus = null;
  appliedDecisionKey = null;
  identityGate = null;
  lastIdentityDetails = null;
  identityWaiters = [];
  if (identityRetryAttached && typeof window !== 'undefined') {
    window.removeEventListener('focus', retryBlockedIdentity);
    window.removeEventListener('online', retryBlockedIdentity);
  }
  identityRetryAttached = false;
  clientListeners.clear();
  storeLinkListeners.clear();
  webIdentityLinkedInMemory = false;
  pendingWebIdentityLinkInMemory = null;
}
